import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './FontColorPicker.module.scss';

interface FontColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  compact?: boolean;
  panelPlacement?: 'side' | 'bottom';
}

const THEME_COLORS = [
  '#000000',
  '#404040',
  '#7a7a7a',
  '#b4b4b4',
  '#dbeafe',
  '#3b82f6',
  '#f97316',
  '#facc15',
  '#6b7280',
  '#65a30d',
];

const STANDARD_COLORS = [
  '#c00000',
  '#ff0000',
  '#ffc000',
  '#ffff00',
  '#92d050',
  '#00b050',
  '#00b0f0',
  '#0070c0',
  '#002060',
  '#7030a0',
];

function FontColorPicker({
  value,
  onChange,
  compact = false,
  panelPlacement = 'side',
}: FontColorPickerProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const colorInputRef = useRef<HTMLInputElement>(null);
  const currentColor = useMemo(
    () => (value?.trim() ? value : '#000000'),
    [value],
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!open) return;
      if (rootRef.current?.contains(event.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className={styles.root} ref={rootRef}>
      <button
        type="button"
        className={compact ? styles.triggerCompact : styles.trigger}
        onClick={() => setOpen((v) => !v)}
        aria-label="Choose text color"
        aria-expanded={open}
      >
        {compact ? (
          <>
            <span className={styles.compactMain} aria-hidden="true">
              <span className={styles.compactA}>A</span>
              <span
                className={styles.compactUnderline}
                style={{ backgroundColor: currentColor }}
              />
            </span>
            <span className={styles.compactCaret} aria-hidden="true">
              ▼
            </span>
          </>
        ) : (
          <>
            <span
              className={styles.colorSwatch}
              style={{ backgroundColor: currentColor }}
            />
            <span className={styles.triggerText}>
              {value ? value : 'Automatic'}
            </span>
            <span className={styles.triggerCaret}>▼</span>
          </>
        )}
      </button>

      {open ? (
        <div
          className={`${styles.panel} ${
            panelPlacement === 'bottom' ? styles.panelBottom : ''
          }`}
        >
          <button
            type="button"
            className={styles.automaticBtn}
            onClick={() => {
              onChange('');
              setOpen(false);
            }}
          >
            Automatic
          </button>

          <div className={styles.sectionTitle}>Theme Colors</div>
          <div className={styles.swatchGrid}>
            {THEME_COLORS.map((color) => (
              <button
                key={`theme-${color}`}
                type="button"
                className={styles.swatchBtn}
                style={{ backgroundColor: color }}
                onClick={() => {
                  onChange(color);
                  setOpen(false);
                }}
                aria-label={`Set color ${color}`}
                title={color}
              />
            ))}
          </div>

          <div className={styles.sectionTitle}>Standard Colors</div>
          <div className={styles.swatchGrid}>
            {STANDARD_COLORS.map((color) => (
              <button
                key={`standard-${color}`}
                type="button"
                className={styles.swatchBtn}
                style={{ backgroundColor: color }}
                onClick={() => {
                  onChange(color);
                  setOpen(false);
                }}
                aria-label={`Set color ${color}`}
                title={color}
              />
            ))}
          </div>

          <button
            type="button"
            className={styles.moreColorsBtn}
            onClick={() => colorInputRef.current?.click()}
          >
            More Colors...
          </button>
          <input
            ref={colorInputRef}
            type="color"
            className={styles.hiddenColorInput}
            value={currentColor}
            onChange={(e) => onChange(e.target.value)}
            aria-label="Choose custom color"
          />
        </div>
      ) : null}
    </div>
  );
}

export default FontColorPicker;
